import React, { useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { GraduationCap, Brain, Users, BookOpen, Award, Trophy, CheckCircle, User, Star } from 'lucide-react';
import { useSubscription } from '@/hooks/useSubscription';
import { useAdmin } from '@/hooks/useAdmin';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useAuthSession } from '@/hooks/useAuthSession';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { SubscriptionPlans } from './subscription/SubscriptionPlans';
import { Progress } from '@/components/ui/progress';
import { LoadingScreen } from '@/components/ui/LoadingScreen';
import { useIsMobile } from '@/hooks/use-mobile';

interface DashboardStats {
  learning_progress: number;
  tutoring_sessions: number;
  active_learners: number;
  learning_paths: number;
}

const Dashboard = () => {
  const { subscription, subscriptionPlans } = useSubscription();
  const { isAdmin, isLoading: isAdminLoading } = useAdmin();
  const { session } = useAuthSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isMobile } = useIsMobile();

  const userName = session?.user?.user_metadata?.full_name || 
                   session?.user?.user_metadata?.first_name || 
                   session?.user?.email?.split('@')[0] || 
                   "User";

  const userNameColor = "#8B5CF6"; // Vivid Purple from the color options

  const userId = session?.user?.id;
  const hasActiveSubscription = subscription?.has_active_subscription || false;
  const isInTrial = subscription?.subscription?.is_trial || false;
  const trialEndDate = subscription?.subscription?.trial_ends_at 
    ? new Date(subscription.subscription.trial_ends_at) 
    : null;
  const currentPeriodEnd = subscription?.subscription?.current_period_end
    ? new Date(subscription.subscription.current_period_end)
    : null;
  const daysLeft = trialEndDate
    ? Math.ceil((trialEndDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : 0;
  const plansAvailable = subscriptionPlans.length > 0;

  const { data: stats = {
    learning_progress: 25,
    tutoring_sessions: 137,
    active_learners: 250,
    learning_paths: 8
  } as DashboardStats, isLoading } = useQuery({
    queryKey: ['dashboard-stats', userId],
    queryFn: async () => {
      if (!userId) throw new Error('No user ID');

      const { data: performanceData, error: performanceError } = await supabase
        .from('performance_history')
        .select('*')
        .eq('user_id', userId);

      if (performanceError) {
        console.error('Error fetching performance:', performanceError);
        throw performanceError;
      }

      const progress = performanceData?.length ? 
        performanceData.reduce((acc, curr) => acc + (curr.score || 0), 0) / performanceData.length : 
        85;

      const { count: sessionsCount } = await supabase
        .from('learning_sessions')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .gte('created_at', new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString());

      const { count: learnersCount } = await supabase
        .from('user_roles')
        .select('*', { count: 'exact', head: true })
        .eq('approved', true);

      const { count: pathsCount } = await supabase
        .from('learning_paths')
        .select('*', { count: 'exact', head: true });

      return {
        learning_progress: Math.round(progress),
        tutoring_sessions: sessionsCount || 12,
        active_learners: learnersCount || 250,
        learning_paths: pathsCount || 8
      };
    },
    enabled: !!userId
  });

  useEffect(() => {
    if (!userId) return;

    const performanceChannel = supabase.channel('dashboard-updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'performance_history',
          filter: `user_id=eq.${userId}`
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
          console.log('Performance data updated');
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'learning_sessions'
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
          console.log('Learning sessions updated');
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(performanceChannel);
    };
  }, [userId, queryClient]);

  const dashboardStats = [
    {
      title: "Learning Progress",
      value: `${stats.learning_progress}%`,
      icon: GraduationCap,
      description: "Overall completion rate",
      color: "dashboard-gradient-1",
      iconColor: "text-blue-500",
      valueColor: "text-blue-600",
      progressColor: "bg-blue-500",
      progress: stats.learning_progress
    },
    {
      title: "AI Tutoring Sessions",
      value: stats.tutoring_sessions.toString(),
      icon: Brain,
      description: "Sessions this month",
      color: "dashboard-gradient-2",
      iconColor: "text-purple-500",
      valueColor: "text-purple-600",
      progressColor: "bg-purple-500",
      progress: 80
    },
    {
      title: "Community",
      value: `${stats.active_learners}+`,
      icon: Users,
      description: "Active learners",
      color: "dashboard-gradient-3",
      iconColor: "text-teal-500",
      valueColor: "text-teal-600",
      progressColor: "bg-teal-500",
      progress: 65
    },
    {
      title: "Learning Paths",
      value: stats.learning_paths.toString(),
      icon: BookOpen,
      description: "Available paths",
      color: "dashboard-gradient-4",
      iconColor: "text-amber-500",
      valueColor: "text-amber-600",
      progressColor: "bg-amber-500",
      progress: 40
    }
  ];

  const handleUpgrade = () => {
    toast.info('Upgrade flow will be implemented here');
  };

  const renderSubscriptionContent = () => {
    if (isInTrial && trialEndDate) {
      return (
        <div className="mt-2 space-y-2">
          <p className="text-sm text-emerald-700">
            You have full access to all features for {daysLeft} {daysLeft === 1 ? 'day' : 'days'}!
          </p>
          <p className="text-sm font-medium text-emerald-900">
            Trial ends: {trialEndDate.toLocaleDateString()}
          </p>
          <div className="mt-4">
            <Button 
              className="w-full edaptica-button"
              onClick={() => navigate('/subscription/plans')}
            >
              Upgrade Now
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className="mt-2 space-y-2">
        <p className={`text-sm ${hasActiveSubscription ? 'text-emerald-700' : 'text-secondary'}`}>
          {hasActiveSubscription
            ? `You're on the ${subscription?.subscription?.plan} plan`
            : 'Upgrade to access premium features'}
        </p>
        {hasActiveSubscription && currentPeriodEnd ? (
          <p className="text-sm font-medium text-emerald-900">
            Next billing date: {currentPeriodEnd.toLocaleDateString()}
          </p>
        ) : (
          <div className="mt-4">
            <Button 
              className="w-full edaptica-button"
              onClick={() => navigate('/subscription/plans')}
            >
              Upgrade Now
            </Button>
          </div>
        )}
      </div>
    );
  };

  const renderSubscriptionCard = () => {
    if (isAdminLoading) {
      return (
        <Card className="col-span-3 bg-white">
          <CardHeader>
            <CardTitle className="text-2xl font-bold text-center">Loading...</CardTitle>
          </CardHeader>
        </Card>
      );
    }

    const cardClasses = `col-span-3 ${
      isInTrial
        ? 'bg-gradient-to-r from-accent-50 to-primary-50 border-accent-200'
        : hasActiveSubscription
          ? 'bg-gradient-to-r from-accent-50 to-primary-50 border-accent-200'
          : 'bg-gradient-to-r from-primary-50 to-secondary-50 border-primary-200'
    } ${!isAdmin && hasActiveSubscription ? 'border-2' : ''}`;

    const titleClasses = `${isAdmin ? 'text-2xl font-bold text-center' : 'text-lg font-medium'} ${
      hasActiveSubscription || isInTrial
        ? 'text-primary-800'
        : 'text-secondary-800'
    }`;

    let title = 'No Active Subscription';
    if (hasActiveSubscription && !isInTrial) {
      title = 'Active Subscription';
    } else if (isInTrial) {
      title = 'Trial Active';
    }

    return (
      <Card className={cardClasses}>
        <CardHeader>
          <CardTitle className={titleClasses}>
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <SubscriptionPlans />
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-4 sm:space-y-8 dashboard-page">
      <div className="border-b pb-4 sm:pb-6">
        <h1 className={`${isMobile ? "text-xl" : "text-3xl"} font-bold tracking-tight gradient-text`}>
          My Dashboard, 
          <span 
            style={{ color: userNameColor }} 
            className="ml-2 font-semibold"
          >
            <User className={`inline-block mr-2 ${isMobile ? "h-4 w-4" : "h-6 w-6"}`} />
            {userName}
          </span>
        </h1>
        <p className={`text-gray-500 mt-2 ${isMobile ? "text-sm" : ""}`}>
          Track your learning progress and manage your subscription
          {subscription?.subscription?.is_trial && (
            <span className="ml-2 text-yellow-600 font-medium">
              (Trial Active)
            </span>
          )}
        </p>
      </div>
      
      <div className="grid gap-3 sm:gap-6 grid-cols-2 lg:grid-cols-4">
        {dashboardStats.map((stat) => (
          <Card key={stat.title} className={`dashboard-stat-card ${stat.color} border-none shadow-sm`}>
            <CardHeader className={`flex flex-row items-center justify-between space-y-0 ${isMobile ? "p-3 pb-0" : "pb-0"}`}>
              <CardTitle className={`${isMobile ? "text-xs" : "text-base"} font-medium text-gray-700`}>
                {stat.title}
              </CardTitle>
              <div className={`${isMobile ? "p-1.5" : "p-2"} rounded-full ${stat.color.replace('dashboard-gradient', 'bg-gradient-to-r from-blue-100 to-purple-100')}`}>
                <stat.icon className={`${isMobile ? "h-3 w-3" : "h-5 w-5"} ${stat.iconColor}`} />
              </div>
            </CardHeader>
            <CardContent className={isMobile ? "p-3" : "p-6"}>
              <div className={`${isMobile ? "text-xl" : "text-3xl"} font-bold ${stat.valueColor}`}>{stat.value}</div>
              <p className={`${isMobile ? "text-[10px]" : "text-xs"} text-gray-500 mt-1`}>
                {stat.description}
              </p>
              <div className="mt-2 sm:mt-3">
                <Progress value={stat.progress} className="h-1.5" indicatorClassName={stat.progressColor} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-3">
        <Card className="col-span-full md:col-span-2 lg:col-span-2 overflow-hidden">
          <CardHeader className={`bg-gradient-to-r from-purple-50 to-indigo-50 border-b ${isMobile ? "p-3 pb-6" : "pb-8"}`}>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle className={`${isMobile ? "text-lg" : "text-2xl"} font-bold text-purple-900`}>Learning Overview</CardTitle>
                <p className={`text-purple-700 ${isMobile ? "text-xs" : "text-sm"} mt-1`}>Your personal learning journey</p>
              </div>
              <div className="flex space-x-2">
                <Award className={`${isMobile ? "h-7 w-7" : "h-10 w-10"} text-purple-500`} />
                <Trophy className={`${isMobile ? "h-7 w-7" : "h-10 w-10"} text-amber-500`} />
              </div>
            </div>
          </CardHeader>
          <CardContent className={isMobile ? "p-3" : "p-6"}>
            <div className="space-y-4 sm:space-y-6">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className={`${isMobile ? "text-xs" : ""} font-medium text-gray-700`}>AI Fundamentals</span>
                  <span className={`${isMobile ? "text-xs" : ""} text-green-600 font-medium`}>85% Complete</span>
                </div>
                <Progress value={85} className="h-2" indicatorClassName="bg-green-500" />
              </div>
              
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className={`${isMobile ? "text-xs" : ""} font-medium text-gray-700`}>Machine Learning Basics</span>
                  <span className={`${isMobile ? "text-xs" : ""} text-blue-600 font-medium`}>62% Complete</span>
                </div>
                <Progress value={62} className="h-2" indicatorClassName="bg-blue-500" />
              </div>
              
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className={`${isMobile ? "text-xs" : ""} font-medium text-gray-700`}>Deep Learning</span>
                  <span className={`${isMobile ? "text-xs" : ""} text-purple-600 font-medium`}>38% Complete</span>
                </div>
                <Progress value={38} className="h-2" indicatorClassName="bg-purple-500" />
              </div>
              
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className={`${isMobile ? "text-xs" : ""} font-medium text-gray-700`}>Natural Language Processing</span>
                  <span className={`${isMobile ? "text-xs" : ""} text-amber-600 font-medium`}>17% Complete</span>
                </div>
                <Progress value={17} className="h-2" indicatorClassName="bg-amber-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="overflow-hidden">
          <CardHeader className={`bg-gradient-to-r from-blue-50 to-teal-50 border-b ${isMobile ? "p-3 pb-4" : "pb-6"}`}>
            <CardTitle className={`${isMobile ? "text-base" : "text-xl"} font-bold text-blue-900 flex items-center gap-2`}>
              <CheckCircle className={`${isMobile ? "h-4 w-4" : "h-5 w-5"} text-blue-500`} />
              Recent Achievements
            </CardTitle>
          </CardHeader>
          <CardContent className={isMobile ? "p-3" : "p-6"}>
            <ul className="space-y-3 sm:space-y-4">
              <li className="flex items-start space-x-2 sm:space-x-3">
                <div className={`${isMobile ? "p-1" : "p-1.5"} rounded-full bg-green-100`}>
                  <Star className={`${isMobile ? "h-3 w-3" : "h-4 w-4"} text-green-600`} />
                </div>
                <div>
                  <p className={`${isMobile ? "text-sm" : ""} font-medium text-gray-800`}>Perfect Score</p>
                  <p className={`${isMobile ? "text-[10px]" : "text-xs"} text-gray-500`}>Answered all questions correctly</p>
                </div>
              </li>
              
              <li className="flex items-start space-x-2 sm:space-x-3">
                <div className={`${isMobile ? "p-1" : "p-1.5"} rounded-full bg-purple-100`}>
                  <Star className={`${isMobile ? "h-3 w-3" : "h-4 w-4"} text-purple-600`} />
                </div>
                <div>
                  <p className={`${isMobile ? "text-sm" : ""} font-medium text-gray-800`}>Quick Learner</p>
                  <p className={`${isMobile ? "text-[10px]" : "text-xs"} text-gray-500`}>Completed 5 lessons in one day</p>
                </div>
              </li>
              
              <li className="flex items-start space-x-2 sm:space-x-3">
                <div className={`${isMobile ? "p-1" : "p-1.5"} rounded-full bg-blue-100`}>
                  <Star className={`${isMobile ? "h-3 w-3" : "h-4 w-4"} text-blue-600`} />
                </div>
                <div>
                  <p className={`${isMobile ? "text-sm" : ""} font-medium text-gray-800`}>Consistent Progress</p>
                  <p className={`${isMobile ? "text-[10px]" : "text-xs"} text-gray-500`}>Studied for 7 days in a row</p>
                </div>
              </li>
            </ul>
          </CardContent>
        </Card>
      </div>

      <div className="mt-4 sm:mt-8">
        {renderSubscriptionCard()}
      </div>
    </div>
  );
};

export default Dashboard;
